import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Log error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      // Example: logErrorToService(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <div className="text-6xl mb-4">😕</div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-2">Oops! Something went wrong</h2>
              <p className="text-neutral-600 mb-8">
                We're sorry for the inconvenience. An unexpected error occurred while loading the page.
              </p>
              
              <div className="space-y-4">
                <button
                  onClick={this.handleRetry}
                  className="w-full bg-accent-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-accent-600 transition-colors"
                >
                  Try Again
                </button>
                
                <button
                  onClick={this.handleReload}
                  className="w-full bg-neutral-200 text-neutral-700 py-3 px-4 rounded-lg font-semibold hover:bg-neutral-300 transition-colors"
                >
                  Reload Page
                </button>
                
                <a
                  href="/"
                  className="block w-full bg-accent-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-accent-700 transition-colors text-center"
                >
                  Go to Homepage
                </a>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-8 text-left">
                  <summary className="text-sm text-neutral-500 cursor-pointer hover:text-neutral-700 mb-2">
                    Error Details (Development)
                  </summary>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
                    <h4 className="font-semibold text-red-800 mb-2">Error:</h4>
                    <pre className="text-red-700 whitespace-pre-wrap mb-4">
                      {this.state.error.toString()}
                    </pre>
                    
                    {this.state.errorInfo && (
                      <>
                        <h4 className="font-semibold text-red-800 mb-2">Component Stack:</h4>
                        <pre className="text-red-700 whitespace-pre-wrap text-xs">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}
              
              <div className="mt-8 text-sm text-neutral-500">
                <p>
                  If this problem persists, please contact our support team at{' '}
                  <a 
                    href="mailto:democrackers@gmail.com" 
                    className="text-accent-500 hover:text-accent-600"
                  >
                    democrackers@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;